import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import { 
  FaBuilding, 
  FaCar, 
  FaUpload, 
  FaCheck, 
  FaSave, 
  FaArrowLeft, 
  FaPhone, 
  FaMapMarkerAlt, 
  FaGlobe, 
  FaPercentage, 
  FaMoneyBillWave,
  FaEdit
} from "react-icons/fa";
import { toast } from "react-toastify";
import { getBackendBaseUrl } from "../../utils/apiUrl";
import { useTheme } from "../BrandColors/Context/ThemeContext";
import { compressLogoImage } from "../../utils/imageCompressor";

const ProfileCard = () => {
  const navigate = useNavigate();
  const theme = useTheme() || "#3b82f6";
  const [isModalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");

  const [profileData, setProfileData] = useState({
    firstName: storedUser.firstName || storedUser.name || "Dealership Admin",
    lastName: storedUser.lastName || "",
    email: storedUser.email || "admin@dealership.com",
    role: storedUser.role || "DealershipAdmin",
    password: "••••••••",
    dealershipName: "Pohanka Automotive Group",
    manufacturerName: "Toyota",
    logoUrl: "",
    tagline: "Driven by Excellence & Trust",
    phone: "(555) 234-5678",
    address: "123 Main Street, Salisbury, MD 21801",
    website: "https://www.pohanka.com",
    docFee: 800,
    registrationFee: 772,
    taxRate: 7.0
  });

  const [showPassword, setShowPassword] = useState(false);
  const [tempProfileData, setTempProfileData] = useState({ ...profileData });

  const fetchProfileAndDealership = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${getBackendBaseUrl()}/dealerships/current/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && data.data) {
        const u = data.data.user || storedUser;
        const d = data.data.dealership || {};
        
        const merged = {
          firstName: u.firstName || u.name || profileData.firstName,
          lastName: u.lastName || "",
          email: u.email || profileData.email,
          role: u.role || profileData.role,
          password: "••••••••",
          dealershipName: d.dealershipName || profileData.dealershipName,
          manufacturerName: d.manufacturerName || profileData.manufacturerName,
          logoUrl: d.logoUrl || d.avatar || "",
          tagline: d.tagline || profileData.tagline,
          phone: d.phone || profileData.phone,
          address: d.address || profileData.address,
          website: d.website || profileData.website,
          docFee: d.docFee || 800,
          registrationFee: d.registrationFee || 772,
          taxRate: d.taxRate ? (d.taxRate < 1 ? Number((d.taxRate * 100).toFixed(2)) : d.taxRate) : 7.0
        };

        setProfileData(merged);
        setTempProfileData(merged);
      }
    } catch (err) {
      console.warn("Could not load current dealership, using defaults:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfileAndDealership();
  }, []);

  const handleEdit = () => {
    setTempProfileData({ ...profileData });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTempProfileData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const compressed = await compressLogoImage(file, { maxWidth: 360, maxHeight: 120, quality: 0.88 });
        setTempProfileData((prev) => ({ ...prev, logoUrl: compressed }));
      } catch (err) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setTempProfileData((prev) => ({ ...prev, logoUrl: reader.result }));
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    const toastId = toast.loading("Saving Dealership Name & Branding...");
    try {
      const token = localStorage.getItem("authToken");
      let optimizedLogo = tempProfileData.logoUrl;
      if (optimizedLogo && optimizedLogo.startsWith("data:")) {
        try {
            optimizedLogo = await compressLogoImage(optimizedLogo, { maxWidth: 360, maxHeight: 120, quality: 0.88 });
        } catch(err) {
            console.error("Compression failed, using original:", err);
        }
      }
      const payload = {
        dealershipName: tempProfileData.dealershipName,
        logoUrl: optimizedLogo,
        tagline: tempProfileData.tagline,
        manufacturerName: tempProfileData.manufacturerName,
        phone: tempProfileData.phone,
        address: tempProfileData.address,
        website: tempProfileData.website,
        docFee: Number(tempProfileData.docFee) || 800,
        registrationFee: Number(tempProfileData.registrationFee) || 772,
        taxRate: (Number(tempProfileData.taxRate) || 7) / 100,
        firstName: tempProfileData.firstName,
        lastName: tempProfileData.lastName
      };

      const res = await fetch(`${getBackendBaseUrl()}/dealerships/current/profile`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (data.success) {
        setProfileData(tempProfileData);
        toast.update(toastId, {
          render: "✓ Dealership Name & Branding updated successfully! Visible across all Kiosks.",
          type: "success",
          isLoading: false,
          autoClose: 3000
        });
        setModalOpen(false);
      } else {
        toast.update(toastId, {
          render: data.message || "Failed to update dealership branding",
          type: "error",
          isLoading: false,
          autoClose: 3000
        });
      }
    } catch (err) {
      toast.update(toastId, {
        render: "Failed to connect to server.",
        type: "error",
        isLoading: false,
        autoClose: 3000
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto w-full p-4 sm:p-8 animate-fade-in-up font-sans">
      
      {/* Top Header Card */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-3xl p-6 sm:p-8 text-white shadow-xl mb-6 relative overflow-hidden border border-slate-700/60">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6 relative z-10">
          
          <div className="flex items-center gap-5">
            {/* Dealership Logo / Avatar Container */}
            <div className="w-24 h-24 rounded-2xl bg-white p-2 border-2 border-cyan-400 shadow-2xl flex items-center justify-center overflow-hidden flex-shrink-0">
              {profileData.logoUrl ? (
                <img
                  src={profileData.logoUrl}
                  alt={profileData.dealershipName}
                  className="max-h-full max-w-full object-contain"
                />
              ) : (
                <div className="w-full h-full bg-slate-900 text-cyan-400 flex flex-col items-center justify-center rounded-xl">
                  <FaBuilding size={28} />
                  <span className="text-[8px] font-black uppercase mt-1">NO LOGO</span>
                </div>
              )}
            </div>

            <div className="space-y-1 text-center sm:text-left">
              <div className="flex items-center gap-2 justify-center sm:justify-start">
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-cyan-500/20 text-cyan-300 border border-cyan-500/40">
                  {profileData.role}
                </span>
                <span className="text-xs text-slate-400">&middot; {profileData.manufacturerName || "Automotive"}</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                {profileData.dealershipName}
              </h1>
              <p className="text-xs text-slate-300 font-medium">
                {profileData.tagline || "Authorized Salesense.ai Kiosk Partner"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleEdit}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-300 hover:to-blue-400 text-slate-950 font-black text-xs uppercase tracking-wider shadow-lg hover:scale-105 active:scale-95 transition-all flex items-center gap-2 cursor-pointer"
            >
              <FaEdit size={13} /> Edit Name & Logo
            </button>
            <button
              onClick={() => navigate("/d2-dashboard")}
              className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold border border-slate-700 transition-colors cursor-pointer"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      {/* Grid: Dealership Information & Admin Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        
        {/* Dealership Branding & Store Profile */}
        <div className="bg-white rounded-2xl border border-slate-200/70 p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <h3 className="text-sm font-extrabold text-slate-800 flex items-center gap-2">
              <FaBuilding className="text-indigo-600" /> Dealership Profile & Showroom Info
            </h3>
            <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200/60">
              Live on Kiosk
            </span>
          </div>

          <div className="space-y-3 text-xs">
            <div className="flex justify-between py-1 border-b border-slate-100">
              <span className="text-slate-500 font-medium">Dealership Name</span>
              <span className="font-bold text-slate-900">{profileData.dealershipName}</span>
            </div>
            <div className="flex justify-between py-1 border-b border-slate-100">
              <span className="text-slate-500 font-medium">Manufacturer / OEM</span>
              <span className="font-bold text-slate-900">{profileData.manufacturerName}</span>
            </div>
            <div className="flex justify-between py-1 border-b border-slate-100">
              <span className="text-slate-500 font-medium">Tagline / Motto</span>
              <span className="font-bold text-slate-900">{profileData.tagline}</span>
            </div>
            <div className="flex justify-between py-1 border-b border-slate-100">
              <span className="text-slate-500 font-medium">Phone Number</span>
              <span className="font-bold text-slate-900">{profileData.phone}</span>
            </div>
            <div className="flex justify-between py-1 border-b border-slate-100">
              <span className="text-slate-500 font-medium">Showroom Address</span>
              <span className="font-bold text-slate-900 text-right max-w-[200px] truncate">{profileData.address}</span>
            </div>
          </div>
        </div>

        {/* Finance Rules & Administrator Credentials */}
        <div className="bg-white rounded-2xl border border-slate-200/70 p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <h3 className="text-sm font-extrabold text-slate-800 flex items-center gap-2">
              <FaMoneyBillWave className="text-emerald-600" /> Default Fees & Tax Parameters
            </h3>
            <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md">
              Calculator Sync
            </span>
          </div>

          <div className="space-y-3 text-xs">
            <div className="flex justify-between py-1 border-b border-slate-100">
              <span className="text-slate-500 font-medium">Dealer Documentation Fee</span>
              <span className="font-bold text-slate-900">${profileData.docFee.toLocaleString()}</span>
            </div>
            <div className="flex justify-between py-1 border-b border-slate-100">
              <span className="text-slate-500 font-medium">State MV Registration Fee</span>
              <span className="font-bold text-slate-900">${profileData.registrationFee.toLocaleString()}</span>
            </div>
            <div className="flex justify-between py-1 border-b border-slate-100">
              <span className="text-slate-500 font-medium">State Sales Tax Rate</span>
              <span className="font-bold text-slate-900">{profileData.taxRate}%</span>
            </div>
            <div className="flex justify-between py-1 border-b border-slate-100">
              <span className="text-slate-500 font-medium">Administrator Name</span>
              <span className="font-bold text-slate-900">{profileData.firstName} {profileData.lastName}</span>
            </div>
            <div className="flex justify-between py-1 border-b border-slate-100">
              <span className="text-slate-500 font-medium">Login Email</span>
              <span className="font-bold text-slate-900">{profileData.email}</span>
            </div>
          </div>
        </div>
      </div>

      {/* EDIT MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto">
          <div className="bg-[#10172a] border border-cyan-500/30 w-full max-w-xl p-6 sm:p-8 rounded-3xl shadow-2xl space-y-5 text-white my-auto max-h-[90vh] overflow-y-auto">
            
            <div className="flex justify-between items-center border-b border-slate-700 pb-3">
              <div>
                <h2 className="text-xl font-black text-white">Edit Dealership & Logo</h2>
                <p className="text-xs text-slate-400 mt-0.5">
                  Changes update immediately across Kiosk, Mobile screens, and Buyer's Orders.
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                className="text-slate-400 hover:text-white p-1 rounded-lg text-lg"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4 text-xs">
              
              {/* DEALERSHIP LOGO UPLOAD & PREVIEW */}
              <div className="space-y-2 bg-[#090f1f] p-4 rounded-2xl border border-cyan-500/20">
                <label className="block text-[11px] font-bold text-cyan-400 uppercase tracking-wider">
                  Dealership Logo Image (PNG / JPG / SVG)
                </label>
                
                <div className="flex items-center gap-4">
                  {/* Image Preview Box */}
                  <div className="w-24 h-16 rounded-xl bg-white border border-slate-600 flex items-center justify-center p-1 overflow-hidden flex-shrink-0">
                    {tempProfileData.logoUrl ? (
                      <img
                        src={tempProfileData.logoUrl}
                        alt="Preview"
                        className="max-h-full max-w-full object-contain"
                      />
                    ) : (
                      <span className="text-[9px] text-slate-400 font-bold text-center">No Logo Uploaded</span>
                    )}
                  </div>

                  <div className="flex-1 space-y-2">
                    <input
                      id="logo-upload-modal"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoFileUpload}
                      style={{ display: "none" }}
                    />
                    <label
                      htmlFor="logo-upload-modal"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 border border-cyan-500/40 text-xs font-bold cursor-pointer transition-colors"
                    >
                      <FaUpload size={11} /> 📁 Upload Logo Image
                    </label>
                    <input
                      type="text"
                      name="logoUrl"
                      placeholder="Or paste Logo Image URL (https://...)"
                      value={tempProfileData.logoUrl}
                      onChange={handleInputChange}
                      className="w-full bg-[#131b2e] border border-slate-700 rounded-xl px-3 py-1.5 text-xs text-white outline-none focus:border-cyan-400"
                    />
                  </div>
                </div>
              </div>

              {/* DEALERSHIP NAME & MANUFACTURER */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="block text-[11px] font-bold text-slate-300">Dealership Name *</label>
                  <input
                    type="text"
                    name="dealershipName"
                    value={tempProfileData.dealershipName}
                    onChange={handleInputChange}
                    placeholder="e.g. Pohanka Automotive Group"
                    className="w-full bg-[#131b2e] border border-slate-700 rounded-xl p-2.5 text-xs text-white outline-none focus:border-cyan-400"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-[11px] font-bold text-slate-300">Manufacturer / OEM</label>
                  <input
                    type="text"
                    name="manufacturerName"
                    value={tempProfileData.manufacturerName}
                    onChange={handleInputChange}
                    placeholder="e.g. Toyota, Honda, Ford"
                    className="w-full bg-[#131b2e] border border-slate-700 rounded-xl p-2.5 text-xs text-white outline-none focus:border-cyan-400"
                  />
                </div>
              </div>

              {/* TAGLINE & PHONE */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="block text-[11px] font-bold text-slate-300">Tagline / Motto</label>
                  <input
                    type="text"
                    name="tagline"
                    value={tempProfileData.tagline}
                    onChange={handleInputChange}
                    placeholder="e.g. Driven by Excellence"
                    className="w-full bg-[#131b2e] border border-slate-700 rounded-xl p-2.5 text-xs text-white outline-none focus:border-cyan-400"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-[11px] font-bold text-slate-300">Phone Number</label>
                  <input
                    type="text"
                    name="phone"
                    value={tempProfileData.phone}
                    onChange={handleInputChange}
                    placeholder="e.g. (555) 234-5678"
                    className="w-full bg-[#131b2e] border border-slate-700 rounded-xl p-2.5 text-xs text-white outline-none focus:border-cyan-400"
                  />
                </div>
              </div>

              {/* ADDRESS */}
              <div className="space-y-1">
                <label className="block text-[11px] font-bold text-slate-300">Showroom Address</label>
                <input
                  type="text"
                  name="address"
                  value={tempProfileData.address}
                  onChange={handleInputChange}
                  placeholder="e.g. 123 Main Street, Salisbury, MD 21801"
                  className="w-full bg-[#131b2e] border border-slate-700 rounded-xl p-2.5 text-xs text-white outline-none focus:border-cyan-400"
                />
              </div>

              {/* FEES & TAX RATES */}
              <div className="grid grid-cols-3 gap-3 pt-1">
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold text-slate-300">Doc Fee ($)</label>
                  <input
                    type="number"
                    name="docFee"
                    value={tempProfileData.docFee}
                    onChange={handleInputChange}
                    className="w-full bg-[#131b2e] border border-slate-700 rounded-xl p-2 text-xs text-white outline-none focus:border-cyan-400 font-mono"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-[10px] font-bold text-slate-300">Reg Fee ($)</label>
                  <input
                    type="number"
                    name="registrationFee"
                    value={tempProfileData.registrationFee}
                    onChange={handleInputChange}
                    className="w-full bg-[#131b2e] border border-slate-700 rounded-xl p-2 text-xs text-white outline-none focus:border-cyan-400 font-mono"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-[10px] font-bold text-slate-300">Tax Rate (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    name="taxRate"
                    value={tempProfileData.taxRate}
                    onChange={handleInputChange}
                    className="w-full bg-[#131b2e] border border-slate-700 rounded-xl p-2 text-xs text-white outline-none focus:border-cyan-400 font-mono"
                  />
                </div>
              </div>

              {/* ADMIN FIRST & LAST NAME */}
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-800">
                <div className="space-y-1">
                  <label className="block text-[11px] font-bold text-slate-300">Admin First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    value={tempProfileData.firstName}
                    onChange={handleInputChange}
                    className="w-full bg-[#131b2e] border border-slate-700 rounded-xl p-2.5 text-xs text-white outline-none focus:border-cyan-400"
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-[11px] font-bold text-slate-300">Admin Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    value={tempProfileData.lastName}
                    onChange={handleInputChange}
                    className="w-full bg-[#131b2e] border border-slate-700 rounded-xl p-2.5 text-xs text-white outline-none focus:border-cyan-400"
                  />
                </div>
              </div>

            </div>

            {/* ACTION BUTTONS */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
              <button
                type="button"
                onClick={handleCloseModal}
                className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold cursor-pointer transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={handleSaveChanges}
                className="px-6 py-2 rounded-xl bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-300 hover:to-blue-400 text-slate-950 text-xs font-black uppercase tracking-wider shadow-lg cursor-pointer transition-all flex items-center gap-1.5"
              >
                <FaSave size={12} /> {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default ProfileCard;
